import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Camera, Circle, Square, X, Eye, ZoomIn, Flashlight, ScanEye, Layers, ChevronDown, ChevronUp, RotateCcw, RotateCw, Save, RefreshCw } from 'lucide-react';
import { openVis } from '../lib/vis';
import { rotateFree } from '../lib/model';

const MIN_KEY = 'giss_cam_min';
const toFile = async (src) => { const b = await (await fetch(src)).blob(); return new File([b], `foto-${Date.now()}.jpg`, { type: 'image/jpeg' }); };

// after the shot: full-screen preview, rotate left / right, retake or save
function Review({ t, src, setSrc, onSave, onRetake }) {
  const rot = async (a) => setSrc((await rotateFree(src, a)).data);
  return (
    <div className="ghost-review" data-testid="ghost-review">
      <img src={src} alt="" data-testid="ghost-review-img" />
      <div className="row ghost-review-bar">
        <button className="btn btn-outline" data-testid="ghost-rot-left" onClick={() => rot(-90)}><RotateCcw size={18} /> {t.camRotL}</button>
        <button className="btn btn-outline" data-testid="ghost-rot-right" onClick={() => rot(90)}><RotateCw size={18} /> {t.camRotR}</button>
        <button className="btn btn-outline" data-testid="ghost-retake" onClick={onRetake}><RefreshCw size={18} /> {t.camRetake}</button>
        <button className="btn btn-green" data-testid="ghost-save" onClick={onSave}><Save size={18} /> {t.camSave}</button>
      </div>
    </div>
  );
}

const pickMime = () => ['video/mp4', 'video/webm;codecs=vp9,opus', 'video/webm'].find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || '';

const ZOOMS = [1, 2, 3, 5];

// hardware zoom where available, rest digital (CSS preview + cropped capture)
function useCamControls(streamRef, ready) {
  const [zoom, setZoomState] = useState(1);
  const [hw, setHw] = useState(1);
  const [torch, setTorch] = useState(false);
  const track = () => streamRef.current?.getVideoTracks()[0];
  const caps = ready ? track()?.getCapabilities?.() || {} : {};
  const setZoom = async (z) => {
    setZoomState(z);
    if (!caps.zoom) return;
    const v = Math.min(Math.max(z, caps.zoom.min || 1), caps.zoom.max || 1);
    try { await track().applyConstraints({ advanced: [{ zoom: v }] }); setHw(v); } catch { setHw(1); }
  };
  const toggleTorch = async () => {
    try { await track().applyConstraints({ advanced: [{ torch: !torch }] }); setTorch(!torch); } catch { return false; }
    return true;
  };
  return { zoom, setZoom, digital: Math.max(1, zoom / hw), torch, hasTorch: !!caps.torch, toggleTorch };
}

const cropDraw = (ctx, v, k, w, h) => {
  const sw = v.videoWidth / k, sh = v.videoHeight / k;
  ctx.drawImage(v, (v.videoWidth - sw) / 2, (v.videoHeight - sh) / 2, sw, sh, 0, 0, w, h);
};

export default function GhostCamera({ t, mode, xray: xray0 = false, op0 = 55, review = true, onShot, onClose, onFail }) {
  const [min, setMinS] = useState(() => localStorage.getItem(MIN_KEY) === '1');
  const setMin = (v) => { setMinS(v); localStorage.setItem(MIN_KEY, v ? '1' : '0'); };
  const [shotSrc, setShotSrc] = useState(null);
  const vidRef = useRef(null);
  const streamRef = useRef(null);
  const recRef = useRef(null);
  const [opacity, setOpacity] = useState(op0);
  const [rec, setRec] = useState(false);
  const [secs, setSecs] = useState(0);
  const [ready, setReady] = useState(false);
  const cam = useCamControls(streamRef, ready);
  const drawRef = useRef(null);
  const [xray, setXray] = useState(xray0);
  // x-ray: hide map tiles, live camera behind the drawn network at 100 %
  useEffect(() => {
    document.body.classList.toggle('ghost-xray', xray);
    return () => document.body.classList.remove('ghost-xray');
  }, [xray]);

  useEffect(() => {
    let alive = true;
    if (!navigator.mediaDevices?.getUserMedia) { onFail(); return undefined; }
    navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } }, audio: false })
      .then((s) => { if (!alive) { s.getTracks().forEach((x) => x.stop()); return; } streamRef.current = s; vidRef.current.srcObject = s; setReady(true); })
      .catch(() => alive && onFail());
    return () => { alive = false; recRef.current?.state === 'recording' && recRef.current.stop(); streamRef.current?.getTracks().forEach((x) => x.stop()); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!rec) return undefined;
    const id = setInterval(() => setSecs((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [rec]);

  const shoot = () => {
    const v = vidRef.current;
    if (!v?.videoWidth) return;
    const c = document.createElement('canvas');
    c.width = v.videoWidth; c.height = v.videoHeight;
    cropDraw(c.getContext('2d'), v, cam.digital, c.width, c.height);
    if (review) { setShotSrc(c.toDataURL('image/jpeg', 0.9)); return; }
    c.toBlob((b) => b && onShot(new File([b], `foto-${Date.now()}.jpg`, { type: 'image/jpeg' })), 'image/jpeg', 0.9);
  };

  // digital zoom while recording: record from a cropped canvas
  const videoTracks = () => {
    const v = vidRef.current;
    if (cam.digital <= 1.01 || !v.videoWidth) return streamRef.current.getVideoTracks();
    const c = document.createElement('canvas');
    c.width = v.videoWidth; c.height = v.videoHeight;
    const ctx = c.getContext('2d');
    const loop = () => { cropDraw(ctx, v, cam.digital, c.width, c.height); drawRef.current = requestAnimationFrame(loop); };
    loop();
    return c.captureStream(30).getVideoTracks();
  };
  const startRec = async () => {
    let stream = streamRef.current;
    try {
      const a = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream = new MediaStream([...videoTracks(), ...a.getAudioTracks()]);
    } catch { stream = new MediaStream(videoTracks()); }
    const mime = pickMime();
    const r = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    const chunks = [];
    r.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    r.onstop = () => {
      stream.getAudioTracks().forEach((x) => x.stop());
      cancelAnimationFrame(drawRef.current);
      const type = r.mimeType || mime || 'video/webm';
      if (chunks.length) onShot(new File(chunks, `video-${Date.now()}.${type.includes('mp4') ? 'mp4' : 'webm'}`, { type: type.split(';')[0] }));
    };
    r.start(1000);
    recRef.current = r;
    setSecs(0); setRec(true);
  };
  const stopRec = () => { recRef.current?.stop(); setRec(false); };

  return (
    <div className="ghost-cam" data-testid="ghost-camera">
      <video ref={vidRef} autoPlay playsInline muted className="ghost-video" data-testid="ghost-camera-video" style={{ opacity: xray ? 1 : opacity / 100, transform: cam.digital > 1 ? `scale(${cam.digital})` : undefined }} />
      {shotSrc && <Review t={t} src={shotSrc} setSrc={setShotSrc} onRetake={() => setShotSrc(null)} onSave={async () => { const f = await toFile(shotSrc); setShotSrc(null); onShot(f); }} />}
      <div className={`ghost-bar ${min ? 'ghost-min' : ''}`} data-testid="ghost-camera-bar">
        {!min && <>
        <button type="button" className="ghost-fold" data-testid="ghost-fold-btn" aria-expanded onClick={() => setMin(true)}><ChevronDown size={16} /> {t.camLess}</button>
        <p className="ghost-hint">{t.camHint}</p>
        <button className={`chip seg ghost-xray-btn ${xray ? 'on' : ''}`} data-testid="ghost-xray-btn" aria-pressed={xray} onClick={() => setXray(!xray)}>
          <ScanEye size={15} /> {t.camXray}
        </button>
        {!xray && <label className="ghost-slider">
          <Eye size={15} /> <span>{t.camOpacity}</span>
          <input type="range" min="0" max="100" value={opacity} data-testid="ghost-opacity-slider" onChange={(e) => setOpacity(+e.target.value)} />
          <b data-testid="ghost-opacity-value">{opacity}%</b>
        </label>}
        <label className="ghost-slider">
          <ZoomIn size={15} /> <span>{t.camZoom}</span>
          <input type="range" min="1" max="5" step="0.1" value={cam.zoom} data-testid="ghost-zoom-slider" onChange={(e) => cam.setZoom(+e.target.value)} />
          <b data-testid="ghost-zoom-value">{cam.zoom.toFixed(1)}x</b>
        </label>
        <div className="row ghost-actions">
          {ZOOMS.map((z) => <button key={z} className={`chip seg ${cam.zoom === z ? 'on' : ''}`} data-testid={`ghost-zoom-${z}`} onClick={() => cam.setZoom(z)}>{z}x</button>)}
          <button className={`chip seg ${cam.torch ? 'on' : ''}`} data-testid="ghost-torch-btn" onClick={async () => { if (!(await cam.toggleTorch())) toast.message(t.camNoTorch); }}><Flashlight size={14} /> {cam.torch ? t.camTorchOff : t.camTorchOn}</button>
        </div>
        </>}
        <div className="row ghost-actions">
          {min && <button type="button" className="ghost-fold" data-testid="ghost-fold-btn" aria-expanded={false} onClick={() => setMin(false)}><ChevronUp size={16} /> {t.camMore}</button>}
          {!min && !xray && [25, 50, 100].map((v) => <button key={v} className={`chip seg ${opacity === v ? 'on' : ''}`} data-testid={`ghost-opacity-${v}`} onClick={() => setOpacity(v)}>{v}%</button>)}
          {mode === 'video'
            ? (rec
              ? <button className="btn btn-red ghost-shutter" data-testid="ghost-stop-btn" onClick={stopRec}><Square size={16} /> {t.camStop} {secs}s</button>
              : <button className="btn btn-red ghost-shutter" data-testid="ghost-record-btn" onClick={startRec}><Circle size={16} /> {t.camRec}</button>)
            : <button className="btn btn-primary ghost-shutter" data-testid="ghost-shutter-btn" onClick={shoot}><Camera size={18} /> {t.camShot}</button>}
          {!min && <button className="btn btn-outline btn-sm" data-testid="ghost-vis-btn" onClick={openVis}><Layers size={16} /> {t.visShort}</button>}
          <button className="btn btn-outline btn-sm" data-testid="ghost-close-btn" disabled={rec} onClick={onClose}><X size={16} /> {t.camClose}</button>
        </div>
      </div>
    </div>
  );
}
